import { useState } from "react";
import { ReactComponent as SendIcon } from "../../icons/SendIcon.svg";
// import { ReactComponent as AttachIcon } from "../../icons/AttachIcon.svg";
import { CommunitySchema } from "../../data/types";
import { useSendMessage, useLoadMessages, useChatbotTyping, useStaffChatAcknowledgment } from "../../hooks/apphooks";
import { useCompleteSignIn } from "../../hooks/authhooks";
import ChatMessageList from "../../components/ChatMessageList";

interface ChatWindowProps {
  selectedCommunity?: CommunitySchema;
  currentChatCommunity: string;
  username: string;
}

const ChatWindow = ({ selectedCommunity, currentChatCommunity, username }: ChatWindowProps) => {
  const [currentText, updateCurrentText] = useState("");
  const [staffSubmitToken, setStaffSubmitToken] = useState(0);

  const { user } = useCompleteSignIn();
  const loggedinUid = user && user?.uid ? user.uid : "";
  const communityId = selectedCommunity?.id || currentChatCommunity;
  const { messages, isLoadingMessages, isFetchingMessages } = useLoadMessages(
    loggedinUid,
    communityId
  );
  const { sendMessage, isLoadingMessage, pendingMessage } = useSendMessage(loggedinUid, communityId);
  const isMessagesLoading = isLoadingMessages || (isFetchingMessages && !messages);
  const isChatbotTyping = useChatbotTyping(currentChatCommunity, isLoadingMessage);
  const { isStaffTyping, staffAutoReply } = useStaffChatAcknowledgment(
    currentChatCommunity,
    staffSubmitToken,
    messages,
    selectedCommunity?.timezone
  );

  const userLabel =
    user?.first_name || user?.username || username || "You";
  const userAvatar = user?.avatar;

  const handleSubmit = async () => {
    if (communityId && currentText.trim()) {
      await sendMessage(currentText);
      updateCurrentText("");
      if (currentChatCommunity === "staff") {
        setStaffSubmitToken((token) => token + 1);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (currentText.trim()) {
        handleSubmit();
      }
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-white font-open-sans">
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 sm:py-6 bg-[#FAFAFA]">
        <div className="flex flex-col min-h-full justify-end">
          <ChatMessageList
            messages={messages}
            isLoading={isMessagesLoading}
            currentChatCommunity={currentChatCommunity}
            selectedCommunity={selectedCommunity}
            userAvatar={userAvatar}
            userLabel={userLabel}
            variant="mobile"
            isChatbotTyping={isChatbotTyping}
            isStaffTyping={isStaffTyping}
            staffAutoReply={staffAutoReply}
            pendingMessage={pendingMessage}
          />
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-[#E9E9E9] bg-white px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex flex-row items-end gap-2 sm:gap-3">
          {/* <button
            type="button"
            className="flex-shrink-0 my-auto w-10 h-10 sm:w-11 sm:h-11 p-0 flex items-center justify-center bg-white border border-[#E9E9E9] hover:border-primaryDark-300 rounded-full transition-colors active:bg-gray-50 cursor-pointer"
            aria-label="Attach file"
          >
            <AttachIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
          </button> */}

          <div className="flex-1 min-w-0">
            <textarea
              onChange={(e) => updateCurrentText(e.target.value)}
              onKeyPress={handleKeyPress}
              value={currentText}
              rows={1}
              className="w-full min-h-[44px] sm:min-h-[48px] max-h-32 px-3 sm:px-4 py-2.5 sm:py-3 bg-white text-gray-800 my-auto text-sm sm:text-base border border-[#E9E9E9] rounded-xl focus:outline-none focus:ring-2 focus:ring-primaryDark-300 focus:border-transparent resize-none overflow-y-auto font-open-sans placeholder:text-gray-400"
              placeholder="Type a message"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e0 transparent",
              }}
            />
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!currentText.trim()}
            className="flex-shrink-0 my-auto w-10 h-10 border-0 sm:w-11 sm:h-11 p-0 flex items-center justify-center bg-primaryDark-0 hover:bg-primaryDark-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full transition-colors active:scale-95 cursor-pointer"
            aria-label="Send message"
          >
            <SendIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
